import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import { isEqual } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getProgramIdFromName,
  isCTPOForProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { useDebouncedCallback } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useDebouncedCallback'
import { ClientFilters } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/ClientFilters'
import {
  ClientFiltersState,
  ClientFilterStatus,
  ClientFilterType,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/ClientFiltersPopover'
import { HorizontalTabsContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/horizontalTabsContainer/HorizontalTabsContainer'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { AddNewClientsList } from './AddNewClientsList'
import ClientListTable from './ClientListTable'
import ClientSearchInput from './ClientSearchInput'
import DBTTeensFilterDropdown from './DBTTeensFilterDropdown'
import useFilteredClientItems from './hooks/useFilteredClientItems'
import { LegacyProviderPortalTab } from './LegacyProviderPortalTab'
import { mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import ClientsDashboard from '../../../../clients/dashboard/ClientsDashboard'
import { programConfig, ROLES } from '../../../../common/constants/appConstants'
import { hasRole, isEngageDAProvider } from '../../../../common/utils/utils'
import { getAuthProgramTaxonomies, getAuthRoles, getAuthUserCapacityValue } from '../../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../../data/store'
import { getUserClientListFilters } from '../../../../data/userSettingsSelectors'
import { setClientListFilters } from '../../../../data/userSettingsSlice'
import { ClientsInProgramFilter, DBTTeensRelationshipFilter } from '../../data/ltClientsApi'

const OuterContainer = styled.View({
  flexDirection: 'row',
  flex: 1,
  overflow: 'hidden',
})

const Container = styled.View(({ theme }) => ({
  flex: 1,
  padding: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
  gap: theme.spacing['16px'],
  overflow: 'scroll',
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const Cell = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const ClientListV3: React.FC = () => {
  const { isDAProviderMigrated, isAddNewClientsEnabled, isDBTTeensEnabled, isLegacyClientTransitionEnabled } =
    useFlags()
  const capacities = useSelector(getAuthUserCapacityValue)
  const programTaxonomies = useSelector(getAuthProgramTaxonomies)
  const savedFilterState = useSelector(getUserClientListFilters)
  const dispatch = useAppDispatch()
  const { colors } = useTheme()

  const userRoles = useSelector(getAuthRoles)
  const [searchText, setSearchText] = useState('')

  // Tab state is URL-driven for the transition experience only (?tab=engage|legacy); other
  // provider types never read or write the param and a stray value stays inert
  const isTransitionExperienceEnabled = isDAProviderMigrated && isLegacyClientTransitionEnabled
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTabId = tabParam === 'legacy' ? 'legacyProviderPortal' : 'engagePlatform'

  useEffect(() => {
    if (isTransitionExperienceEnabled && tabParam !== 'engage' && tabParam !== 'legacy') {
      const next = new URLSearchParams(searchParams)
      next.set('tab', 'engage')
      setSearchParams(next, { replace: true })
    }
  }, [isTransitionExperienceEnabled, tabParam, searchParams, setSearchParams])

  const handleTransitionSuccess = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'engage')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const programNames = useMemo(
    () =>
      [
        hasRole(userRoles, [ROLES.LT_TEENS_THERAPIST]) ? ProgramNames.TeensTherapy : null,
        hasRole(userRoles, [ROLES.LT_CFP_COACH]) ? ProgramNames.CoachingForParents : null,
        hasRole(userRoles, [ROLES.LT_CGT_COACH]) ? ProgramNames.CaregiverTrainingForNeuro : null,
        hasRole(userRoles, [ROLES.LT_NSC_COACH]) ? ProgramNames.NeurodiversitySkillsCoaching : null,
        hasRole(userRoles, [ROLES.LT_DBT_TEENS_THERAPIST, ROLES.LT_DBT_TEENS_THERAPIST_SUPERVISOR])
          ? ProgramNames.DialecticalBehaviorTherapyTeens
          : null,
        hasRole(userRoles, [ROLES.LT_MANAGER_COACH, ROLES.LT_MANAGER_COACH_SUPERVISOR])
          ? ProgramNames.ManagerCoaching
          : null,
        hasRole(userRoles, [ROLES.DA_INDIVIDUAL_THERAPIST]) ? ProgramNames.DirectAccessIndividualTherapy : null,
        hasRole(userRoles, [ROLES.DA_COUPLES_THERAPIST]) ? ProgramNames.DirectAccessCouplesTherapy : null,
        hasRole(userRoles, [ROLES.DA_FAMILY_THERAPIST]) ? ProgramNames.DirectAccessFamilyTherapy : null,
        hasRole(userRoles, [ROLES.DA_CHILD_THERAPIST]) ? ProgramNames.DirectAccessIndividualChildTherapy : null,
        hasRole(userRoles, [ROLES.DA_ADOLESCENT_THERAPIST])
          ? ProgramNames.DirectAccessIndividualAdolescentTherapy
          : null,
        ...(capacities ? Object.keys(capacities) : []),
      ].filter(Boolean) as ProgramNames[],
    [capacities, userRoles],
  )

  const showDBTTeensFilter =
    isDBTTeensEnabled && programNames.some((name) => programConfig[name as LTProgramName]?.showClientRelationshipFilter)

  // Lyra AI is enabled for every provider who offers LCC Coaching, independent of the AI coach flag
  const showLyraAiFilter = !!programTaxonomies?.some((ctpo) => isCTPOForProgram(ctpo, ProgramNames.Coaching))

  // Ignore any saved Lyra AI selection while the filter is hidden so it can't narrow the list
  const filterState = useMemo(
    () => (showLyraAiFilter ? savedFilterState : { ...savedFilterState, aiCoachStatus: [] }),
    [savedFilterState, showLyraAiFilter],
  )

  const [dbtTeensRelationshipFilter, setDbtTeensRelationshipFilter] = useState<DBTTeensRelationshipFilter>('primary')

  const setSearchTextDebounced = useDebouncedCallback(setSearchText, 500)

  const dbtTeensApiFilter: ClientsInProgramFilter | undefined =
    showDBTTeensFilter && dbtTeensRelationshipFilter !== 'primary' ? dbtTeensRelationshipFilter : undefined
  const clientItems = useFilteredClientItems(
    searchText,
    showDBTTeensFilter ? { ...filterState, status: ClientFilterStatus.ACTIVE } : filterState,
    isEngageDAProvider(userRoles),
    dbtTeensApiFilter,
  )
  const programIds = useMemo(
    () => [...new Set(programNames.map((program) => getProgramIdFromName(program)).filter(Boolean) as string[])],
    [programNames],
  )

  const roleAdjustedFilterState = useMemo(() => {
    const adjusted = { ...filterState }
    if (isEngageDAProvider(userRoles)) {
      delete adjusted.activityStatus
      delete adjusted.episodeProgress
      if (filterState.modality === undefined) {
        adjusted.modality = []
      }
    }
    return adjusted
  }, [filterState, userRoles])

  const availableFilters = useMemo(() => {
    const filters = []
    if (programIds.length > 1) {
      filters.push(ClientFilterType.PROGRAMS)
    }
    if (!hasRole(userRoles, [ROLES.LT_PRESCRIBER])) {
      filters.push(ClientFilterType.EPISODE_PROGRESS)
    }
    if (showLyraAiFilter) {
      filters.push(ClientFilterType.LYRA_AI)
    }
    return filters
  }, [programIds.length, showLyraAiFilter, userRoles])

  useEffect(() => {
    if (!searchText) {
      return
    }

    track({
      event: mixpanelEvents.CLIENT_LIST_SEARCH_CLIENT_NAME,
    })
  }, [searchText])

  const onFilterStateChange = useCallback(
    (newFilterState: ClientFiltersState) => {
      if (roleAdjustedFilterState.status !== newFilterState.status) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_STATUS,
          properties: { filter_value: newFilterState.status },
        })
      }

      if (!isEqual(roleAdjustedFilterState.activityStatus, newFilterState.activityStatus)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_ACTIVITY_STATUS,
          properties: { filter_value: newFilterState.activityStatus },
        })
      }

      if (!isEqual(roleAdjustedFilterState.programs, newFilterState.programs)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_PROGRAM,
          properties: { filter_value: newFilterState.programs },
        })
      }

      if (!isEqual(roleAdjustedFilterState.episodeProgress, newFilterState.episodeProgress)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_EPISODE_PROGRESS,
          properties: { filter_value: newFilterState.episodeProgress },
        })
      }

      if (!isEqual(roleAdjustedFilterState.nextSession, newFilterState.nextSession)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_NEXT_SESSION,
          properties: { filter_value: newFilterState.nextSession },
        })
      }

      if (!isEqual(roleAdjustedFilterState.sessionType, newFilterState.sessionType)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_SESSION_TYPE,
          properties: { filter_value: newFilterState.sessionType },
        })
      }

      if (!isEqual(roleAdjustedFilterState.modality, newFilterState.modality)) {
        track({
          event: mixpanelEvents.CLIENT_LIST_FILTER_MODALITY_TYPE,
          properties: { filter_value: newFilterState.modality },
        })
      }

      dispatch(setClientListFilters(newFilterState))
    },
    [dispatch, roleAdjustedFilterState],
  )

  const engageDAClientsListWithAvailability = (
    <>
      {isAddNewClientsEnabled && <AddNewClientsList />}
      <Cell>
        <ClientSearchInput onChange={setSearchTextDebounced} />
        {showDBTTeensFilter ? (
          <DBTTeensFilterDropdown
            selectedFilter={dbtTeensRelationshipFilter}
            onFilterChange={setDbtTeensRelationshipFilter}
          />
        ) : (
          <ClientFilters
            programs={programIds}
            filterState={roleAdjustedFilterState}
            onFilterStateChange={onFilterStateChange}
            availableFilters={availableFilters}
            isEngageProvider={isEngageDAProvider(userRoles)}
          />
        )}
      </Cell>
      <ClientListTable clients={clientItems} programIds={programIds} providerRoles={userRoles} />
    </>
  )

  const legacyProviderPortalTooltip = (
    <Tooltip
      content={
        <FormattedMessage
          defaultMessage='Clients you were seeing prior to transitioning to the Lyra Engage platform. Continue your current process for notes and scheduling with these clients, and use the Request Payment button below to bill all sessions until you discharge the client.'
          description='Tooltip explaining the Legacy Provider Portal tab for providers'
        />
      }
      hoverEnabled
      style={{ marginLeft: 8 }}
      accessibilityLabel='Information about Legacy Provider Portal'
      testID={tID('ClientListV3-legacyProviderPortal-tooltip')}
    >
      <InfoIcon size={18} fillColor={colors.iconDefault} />
    </Tooltip>
  )

  const tabs = [
    {
      id: 'engagePlatform',
      label: 'Engage Platform',
      content: engageDAClientsListWithAvailability,
      overflowTreatment: 'none',
    },
    {
      id: 'legacyProviderPortal',
      label: 'Legacy Provider Portal',
      content: isLegacyClientTransitionEnabled ? (
        <LegacyProviderPortalTab onTransitionSuccess={handleTransitionSuccess} />
      ) : (
        <ClientsDashboard />
      ),
      overflowTreatment: 'visible',
      // The tab's orienting copy moves onto the page itself when the transition experience is on (NETEN-2547)
      iconWithTooltip: isLegacyClientTransitionEnabled ? undefined : legacyProviderPortalTooltip,
    },
  ]

  const handleTabChange = (tabId: string) => {
    if (tabId === 'legacyProviderPortal') {
      track({ event: mixpanelEvents.OPEN_CLIENT_LIST_LEGACY_PROVIDER_PORTAL_TAB })
    } else if (tabId === 'engagePlatform') {
      track({ event: mixpanelEvents.OPEN_CLIENT_LIST_ENGAGE_PLATFORM_TAB })
    }
    if (isTransitionExperienceEnabled) {
      const next = new URLSearchParams(searchParams)
      next.set('tab', tabId === 'legacyProviderPortal' ? 'legacy' : 'engage')
      setSearchParams(next, { replace: true })
    }
  }

  return (
    <OuterContainer>
      <Container testID={tID('ClientListV3-container')}>
        <Title
          size={TitleSize.LARGE}
          text={<FormattedMessage defaultMessage='Clients' description='Clients list title' />}
        />
        {isDAProviderMigrated ? (
          <HorizontalTabsContainer
            tabs={tabs}
            initialTabId='engagePlatform'
            activeTabId={isTransitionExperienceEnabled ? activeTabId : undefined}
            onTabChange={handleTabChange}
          />
        ) : (
          <>
            <Row>
              <ClientSearchInput onChange={setSearchTextDebounced} />
              {showDBTTeensFilter ? (
                <DBTTeensFilterDropdown
                  selectedFilter={dbtTeensRelationshipFilter}
                  onFilterChange={setDbtTeensRelationshipFilter}
                />
              ) : (
                <ClientFilters
                  programs={programIds}
                  filterState={roleAdjustedFilterState}
                  onFilterStateChange={onFilterStateChange}
                  availableFilters={availableFilters}
                  isEngageProvider={isEngageDAProvider(userRoles)}
                />
              )}
            </Row>
            <ClientListTable clients={clientItems} programIds={programIds} providerRoles={userRoles} />
          </>
        )}
      </Container>
    </OuterContainer>
  )
}

export default ClientListV3
